import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft, Upload, Loader2, FolderOpen, ExternalLink,
  Download, Trash2, FileText, AlertCircle, CheckCircle2
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const StoreDocuments = () => {
  const { docketId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const fileInputRef = useRef(null);

  const [pageLoading, setPageLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [docketInfo, setDocketInfo] = useState(null);
  const [driveConnected, setDriveConnected] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [documentTitle, setDocumentTitle] = useState('');
  const [remarks, setRemarks] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return { 'Authorization': `Bearer ${token}` };
  };

  const fetchDocuments = async () => {
    const res = await fetch(`${backendUrl}/api/dockets/${docketId}/stored-documents`, {
      headers: getAuthHeaders()
    });
    if (res.ok) setDocuments(await res.json());
  };

  useEffect(() => {
    const init = async () => {
      try {
        setPageLoading(true);
        const [docketRes, driveRes] = await Promise.all([
          fetch(`${backendUrl}/api/dockets/${docketId}`, { headers: getAuthHeaders() }),
          fetch(`${backendUrl}/api/drive/status`, { headers: getAuthHeaders() })
        ]);

        if (!docketRes.ok) throw new Error('Failed to fetch docket');
        setDocketInfo(await docketRes.json());

        if (driveRes.ok) {
          const driveData = await driveRes.json();
          setDriveConnected(driveData.connected === true);
        }

        await fetchDocuments();
      } catch (err) {
        toast({ title: 'Error', description: err.message, variant: 'destructive' });
      } finally {
        setPageLoading(false);
      }
    };
    if (docketId) init();
  }, [docketId, backendUrl]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      if (!documentTitle) setDocumentTitle(file.name);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast({ title: 'Validation', description: 'Please select a file to upload', variant: 'destructive' });
      return;
    }
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', selectedFile);

      const params = new URLSearchParams();
      if (documentTitle) params.append('document_title', documentTitle);
      if (remarks) params.append('remarks', remarks);

      const res = await fetch(
        `${backendUrl}/api/dockets/${docketId}/stored-documents?${params.toString()}`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: formData
        }
      );

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Upload failed');
      }

      toast({ title: 'Uploaded', description: `${selectedFile.name} uploaded successfully` });
      setSelectedFile(null);
      setDocumentTitle('');
      setRemarks('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      await fetchDocuments();
    } catch (err) {
      toast({ title: 'Upload Failed', description: err.message, variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId, fileName) => {
    if (!window.confirm(`Delete "${fileName}" from Google Drive and records?`)) return;
    try {
      const res = await fetch(`${backendUrl}/api/dockets/${docketId}/stored-documents/${docId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Delete failed');
      toast({ title: 'Deleted', description: `${fileName} removed` });
      await fetchDocuments();
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    }
  };

  const handleDownload = (doc) => {
    const token = localStorage.getItem('auth_token');
    const url = `${backendUrl}/api/drive/files/${doc.drive_file_id}/download?token=${token}`;
    window.open(url, '_blank');
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('en-IN', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch { return iso; }
  };

  const getMimeIcon = (mime) => {
    if (!mime) return '📄';
    if (mime.includes('pdf')) return '📕';
    if (mime.includes('image')) return '🖼️';
    if (mime.includes('sheet') || mime.includes('excel') || mime.includes('csv')) return '📊';
    if (mime.includes('word') || mime.includes('document')) return '📝';
    return '📄';
  };

  if (pageLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent-blue" />
      </div>
    );
  }

  const jobsheetNo = docketInfo?.system_docket_no || docketId;
  const shipmentType = docketInfo?.shipment_type || '—';

  return (
    <div className="min-h-screen bg-surface-light p-6">
      <div className="max-w-5xl mx-auto space-y-6">

        {/* Page Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-xl font-bold text-text-dark font-manrope flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-blue-600" />
              Store Documents
            </h1>
            <p className="text-sm text-text-secondary font-ibm">
              Docket: {jobsheetNo} &nbsp;·&nbsp; Type: {shipmentType}
            </p>
          </div>
        </div>

        {/* Drive Status Banner */}
        {driveConnected ? (
          <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-2 text-sm text-green-700 font-ibm">
            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
            Google Drive connected — files will be stored under
            <span className="font-semibold ml-1">Shipment Documents / {shipmentType} / {jobsheetNo}</span>
          </div>
        ) : (
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 text-sm text-amber-800 font-ibm">
            <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold">Google Drive not connected</p>
              <p className="text-xs mt-0.5">Please connect Google Drive in Settings → Integrations to enable file uploads.</p>
            </div>
          </div>
        )}

        {/* Upload Form */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-5 py-3">
            <h2 className="text-base font-semibold text-white font-ibm">Upload Document</h2>
          </div>

          <div className="p-5 space-y-4">

            {/* Read-only info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Docket / Jobsheet No.</Label>
                <Input value={jobsheetNo} readOnly className="mt-1 bg-gray-50 cursor-not-allowed" data-testid="sd-jobsheet-no" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Shipment Type</Label>
                <Input value={shipmentType} readOnly className="mt-1 bg-gray-50 cursor-not-allowed" data-testid="sd-shipment-type" />
              </div>
            </div>

            {/* Document title + remarks */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Document Title</Label>
                <Input
                  value={documentTitle}
                  onChange={(e) => setDocumentTitle(e.target.value)}
                  placeholder="e.g. Invoice, Packing List, BL Copy"
                  className="mt-1"
                  data-testid="sd-doc-title"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Remarks</Label>
                <Input
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  placeholder="Optional notes"
                  className="mt-1"
                  data-testid="sd-remarks"
                />
              </div>
            </div>

            {/* File picker + upload */}
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label className="text-xs font-semibold text-text-secondary font-ibm">Select File</Label>
                <Input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileChange}
                  className="mt-1"
                  data-testid="sd-file-input"
                  disabled={!driveConnected}
                />
                {selectedFile && (
                  <p className="text-xs text-text-secondary mt-1 font-ibm">
                    Selected: <span className="font-medium text-text-dark">{selectedFile.name}</span>
                    &nbsp;({formatFileSize(selectedFile.size)})
                  </p>
                )}
              </div>
              <Button
                onClick={handleUpload}
                disabled={uploading || !driveConnected || !selectedFile}
                className="bg-blue-600 hover:bg-blue-700 mb-0.5"
                data-testid="sd-upload-btn"
              >
                {uploading
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>
                  : <><Upload className="w-4 h-4 mr-2" />Upload</>
                }
              </Button>
            </div>

          </div>
        </Card>

        {/* Documents Table */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="bg-gray-50 border-b border-gray-200 px-5 py-3">
            <h2 className="text-sm font-semibold text-text-dark font-ibm">
              Uploaded Documents ({documents.length})
            </h2>
          </div>

          {documents.length === 0 ? (
            <div className="p-8 text-center">
              <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-text-secondary font-ibm">No documents uploaded yet.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs font-ibm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 text-left text-text-secondary font-semibold">Sr.</th>
                    <th className="px-3 py-2 text-left text-text-secondary font-semibold">Document Name</th>
                    <th className="px-3 py-2 text-left text-text-secondary font-semibold">Original File</th>
                    <th className="px-3 py-2 text-left text-text-secondary font-semibold">Size</th>
                    <th className="px-3 py-2 text-left text-text-secondary font-semibold">Remarks</th>
                    <th className="px-3 py-2 text-left text-text-secondary font-semibold">Uploaded By</th>
                    <th className="px-3 py-2 text-left text-text-secondary font-semibold">Uploaded At</th>
                    <th className="px-3 py-2 text-left text-text-secondary font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {documents.map((doc, idx) => (
                    <tr key={doc.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-2 text-text-secondary">{idx + 1}</td>
                      <td className="px-3 py-2">
                        <span className="mr-1">{getMimeIcon(doc.mime_type)}</span>
                        <span className="font-medium text-text-dark">{doc.document_title || '—'}</span>
                      </td>
                      <td className="px-3 py-2 text-text-secondary max-w-[150px] truncate" title={doc.original_file_name}>
                        {doc.original_file_name}
                      </td>
                      <td className="px-3 py-2 text-text-secondary whitespace-nowrap">
                        {formatFileSize(doc.file_size)}
                      </td>
                      <td className="px-3 py-2 text-text-secondary max-w-[120px] truncate">{doc.remarks || '—'}</td>
                      <td className="px-3 py-2 text-text-secondary">{doc.uploaded_by || '—'}</td>
                      <td className="px-3 py-2 text-text-secondary whitespace-nowrap">{formatDate(doc.created_at)}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          {doc.drive_view_link && (
                            <Button
                              size="sm" variant="ghost"
                              className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-50"
                              onClick={() => window.open(doc.drive_view_link, '_blank')}
                              title="View in Drive"
                              data-testid={`sd-view-${idx}`}
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          <Button
                            size="sm" variant="ghost"
                            className="h-7 w-7 p-0 text-green-600 hover:bg-green-50"
                            onClick={() => handleDownload(doc)}
                            title="Download"
                            data-testid={`sd-download-${idx}`}
                          >
                            <Download className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            className="h-7 w-7 p-0 text-red-500 hover:bg-red-50"
                            onClick={() => handleDelete(doc.id, doc.original_file_name)}
                            title="Delete"
                            data-testid={`sd-delete-${idx}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

      </div>
    </div>
  );
};

export default StoreDocuments;
